/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { message, Button, Upload } from 'antd';
import { toBase64 } from '../../utils/toBase64';
import { Text } from '../base/Text';
import { useCreatePDF } from '../../networking/useDocuments';
import Loading from '../base/Loading';
const MAX_COUNT_FILES_TO_UPLOAD = 5;
const { Dragger } = Upload;
/**
 * Component to upload documents in bulk.
 * if setImages is defined, the 'accept' prop should contain the 'image/*' content type
 *
 * @param onDrop: function to invoke immediately after the files are dropped
 * @param text: message inside the dragger
 * @param setFiles: function to invoke after the upload is completed. If onDrop is specified, this is overwritten
 * @param documentType: 'knowledge_base' | 'cv-report' | 'report'
 * @param multiple: allow multiple files to be uploaded
 * @param accept: MIME type of the files to be uploaded
 * @param useButton: show a simple button instead of the dragger for the file drop
 * @param loadingText: message to show while loading
 * @param loadingOnlyText: if True, do not show the spinner when loading
 * @param styleText: CSS Properties of the message inside the dragger
 * @param skipSection: section in the FileDrop where you can choose to skip the dropping
 * @param extractElements: if False, avoid the extraction of the chunks, making the upload faster
 * @returns
 */
export const FileDrop = ({ onDrop, text, setFiles, documentType = null, multiple = false, accept = 'application/pdf', useButton = false, loadingText, loadingOnlyText = false, styleText, skipSection, extractElements = true, }) => {
    const [fileList, setFileList] = useState([]);
    const { isLoading, data: uploadResponse, call } = useCreatePDF();
    // const {
    //   isLoading: isLoadingImgClassification,
    //   data: imgClassificationResponse,
    //   call: callImgClassification,
    // } = usePostImageClassification();
    const splitFiles = async (files) => {
        const pdfs = [];
        const images = [];
        for (let file of files) {
            if (file.type === 'application/pdf') {
                pdfs.push({ base64_document: (await toBase64(file)).base64String, filename: file.name });
            }
            else if (['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(file.type)) {
                images.push({ base64_document: (await toBase64(file)).base64String, filename: file.name });
            }
        }
        return { pdfs, images };
    };
    useEffect(() => {
        async function callOnDrop() {
            const { pdfs, images } = await splitFiles(fileList);
            console.log({ pdfs, images });
            onDrop({ pdfs, images });
        }
        async function uploadDocuments() {
            const { pdfs, images } = await splitFiles(fileList);
            call({ documents: pdfs, documents_type: documentType, extract_elements: extractElements });
        }
        if (fileList.length > 0) {
            if (onDrop) {
                callOnDrop();
            }
            else {
                uploadDocuments();
            }
        }
    }, [fileList]);
    useEffect(() => {
        if (uploadResponse && setFiles) {
            setFiles(uploadResponse);
        }
    }, [uploadResponse]);
    const props = {
        accept: accept,
        multiple: multiple,
        showUploadList: false,
        maxCount: MAX_COUNT_FILES_TO_UPLOAD,
        beforeUpload: async (file, fileList) => {
            if (fileList.length >= MAX_COUNT_FILES_TO_UPLOAD) {
                message.error(`You can only upload up to ${MAX_COUNT_FILES_TO_UPLOAD} files at the same time.`);
                return false;
            }
            try {
                setFileList(fileList);
            }
            catch (error) {
                message.error(`Failed to convert files to base64.`);
            }
            return false;
        },
        onChange(info) { },
        onDrop(event) { },
    };
    if (isLoading) {
        return (React.createElement(Loading, { text: loadingText || 'Uploading documents...', style: { paddingTop: 24, fontSize: 14, ...styleText }, absolute: false, onlyText: loadingOnlyText }));
    }
    if (useButton) {
        return (React.createElement(Upload, { ...props, fileList: fileList },
            React.createElement(Button, { icon: React.createElement(UploadOutlined, null) }, text === null ? 'Upload documents' : text)));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Dragger, { ...props, fileList: fileList },
            React.createElement(React.Fragment, null,
                React.createElement("p", { className: "ant-upload-drag-icon" },
                    React.createElement(InboxOutlined, null)),
                React.createElement("div", null),
                React.createElement(Text, { className: "ant-upload-text", value: text || 'Click or drag file to this area to upload', style: { ...styleText } }),
                React.createElement("p", { style: { paddingTop: 10 }, className: "ant-upload-hint" }, 'The documents will only be accessible to you'))),
        skipSection && !isLoading && (React.createElement("span", { style: { padding: 10 } },
            "or",
            React.createElement("span", { className: "clickable blue", style: { marginLeft: 4, fontSize: 14, fontWeight: 400 }, onClick: skipSection.onClick, onKeyDown: () => { }, role: "button", tabIndex: 0 }, skipSection.text)))));
};
export default FileDrop;
